
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { RiskAreaEditComponent } from 'src/app/modules/home/risks/risk-areas/risk-area-edit/risk-area-edit.component';
import { RiskAreaSelectionComponent } from 'src/app/modules/shared/risk-area-selection/risk-area-selection.component';
import { AlertService } from '../../commons/services/alert.service';
import { LaravelRiskAreaService } from '../../commons/services/backend/laravel-risk-area.service';
import * as RiskAreaActions from '../actions/risk-area.actions';
import { AppState } from '../reducers';
import { getRiskAreaDialogId, getRiskAreasTableState, getSelectionDialogId } from '../selectors/risk-area.selectors';
import { TranslateService } from '@ngx-translate/core';



@Injectable()
export class RiskAreaEffects {
  error$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.saveRiskAreaFailed, RiskAreaActions.deleteRiskAreaFailed),
      tap(({ error }) => {
        if (error) {
          this.alertService.showErrorMessage(`${this.translateService.instant('shared.errore')}`, error);
        }
      })
    ), { dispatch: false }
  );

  loadRiskAreas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.loadRiskAreas),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.riskAreaService.list(page, perPage, order, direction, filters, includes)
          .pipe(
            map(result =>
              RiskAreaActions.loadRiskAreasCompleted({ riskAreas: result.data, currentPage: page, total: result.total, perPage, order, direction, filters, includes })
            ),
            catchError(error => {
              return of(RiskAreaActions.loadRiskAreasFailed({ error }))
            })
          )
      })
    )
  );

  changePage = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.changePage),
      withLatestFrom(this.store$.select(getRiskAreasTableState)),
      map(([{ page, size }, { total, currentPage, perPage, direction, order, filters, includes }]) => RiskAreaActions.loadRiskAreas({ page: page, perPage: size, order, direction, filters, includes }))
    )
  );

  changeSort = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.changeSort),
      withLatestFrom(this.store$.select(getRiskAreasTableState)),
      map(([action, { total, currentPage, perPage, direction, order, filters, includes }]) => RiskAreaActions.loadRiskAreas({ page: currentPage, perPage: perPage, order: action.order, direction: action.direction, filters, includes }))
    )
  );

  changeFilters = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.changeFilters),
      withLatestFrom(this.store$.select(getRiskAreasTableState)),
      map(([{ filters }, { total, currentPage, perPage, direction, order, includes }]) => RiskAreaActions.loadRiskAreas({ page: currentPage, perPage: perPage, order, direction, filters, includes }))
    )
  );

  editRiskArea$ = createEffect(() => this.actions$.pipe(
    ofType(RiskAreaActions.editRiskArea),
    map(({ riskArea }) => {
      let dialogRef = this.dialog.open(RiskAreaEditComponent, {
        data: {
          riskArea
        }
      });
      return RiskAreaActions.riskAreaDialogOpened({ dialogId: dialogRef.id });
    }))
  );

  saveRiskArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.saveRiskArea),
      mergeMap(({ riskArea }) =>
        this.riskAreaService.upsert(riskArea.toDTO())
          .pipe(
            map(result =>
              RiskAreaActions.saveRiskAreaCompleted({ riskArea: result })
            ),
            catchError(error => of(RiskAreaActions.saveRiskAreaFailed({ error })))
          )
      )
    )
  );

  onSaveCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.saveRiskAreaCompleted),
      map(action => action.riskArea),
      tap(riskArea => this.alertService.showConfirmMessage(`${riskArea.name} ${this.translateService.instant('shared.salvato con successo')}`)),
      map(() => RiskAreaActions.closeRiskAreaDialog())
    )
  );


  deleteRiskArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.deleteRiskArea),
      switchMap(({ riskArea }) =>
        this.alertService.showConfirmDialog(`${this.translateService.instant('shared.Conferma eliminazione')}`, `${this.translateService.instant('risks.Sei sicuro di voler eliminare aria di rischio')} ${riskArea.name}?`)
          .pipe(
            mergeMap((confirm) => {
              return confirm ?
                this.riskAreaService.delete(riskArea.id)
                  .pipe(
                    map(() => RiskAreaActions.deleteRiskAreaCompleted({ riskArea })),
                    catchError(error => of(RiskAreaActions.deleteRiskAreaFailed({ error })))
                  )

                : of(RiskAreaActions.deleteRiskAreaCancelled());
            })
          )
      )
    )
  );

  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.deleteRiskAreaCompleted),
      tap(({ riskArea }) => this.alertService.showConfirmMessage(` ${riskArea.name} ${this.translateService.instant('shared.eliminato con successo')}`)),
      map(() => RiskAreaActions.closeRiskAreaDialog())
    )
  );


  closeDialog = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.closeRiskAreaDialog),
      withLatestFrom(this.store$.select(getRiskAreaDialogId)),
      tap(([_, dialogId]) => {
        if (dialogId) {
          this.dialog.getDialogById(dialogId).close();
        }
      })
    ), { dispatch: false }
  );

  reloadAfterSave = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.saveRiskAreaCompleted),
      withLatestFrom(this.store$.select(getRiskAreasTableState)),
      map(([_, { currentPage, perPage, direction, order, filters, includes }]) => RiskAreaActions.loadRiskAreas({ page: currentPage, perPage, order, direction, filters, includes }))
    )
  );

  reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.deleteRiskAreaCompleted),
      withLatestFrom(this.store$.select(getRiskAreasTableState)),
      map(([_, { currentPage, perPage, direction, order, filters, includes }]) => RiskAreaActions.loadRiskAreas({ page: currentPage, perPage, order, direction, filters, includes }))
    )
  );

  selectRiskArea$ = createEffect(() => this.actions$.pipe(
    ofType(RiskAreaActions.selectRiskArea),
    map(({ filters }) => {
      let dialogRef = this.dialog.open(RiskAreaSelectionComponent, {
        data: {
          defaultFilters: filters
        }
      });
      return RiskAreaActions.selectionDialogOpened({ selectionDialogId: dialogRef.id });
    }))
  );
  closeSelectionDialog = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.closeSelectionDialog),
      withLatestFrom(this.store$.select(getSelectionDialogId)),
      tap(([_, dialogId]) => {
        if (dialogId) {
          this.dialog.getDialogById(dialogId).close();
        }

      })
    ), { dispatch: false }
  );

  riskAreasSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAreaActions.riskAreaSelected),
      map(() => RiskAreaActions.closeSelectionDialog())
    ))


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private riskAreaService: LaravelRiskAreaService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private translateService: TranslateService
  ) { }
}
